import React, { useState } from 'react';
import { SECTION_CONFIGS, isNormalOpt } from '../constants.js';

const CollapsibleSectionAssessment = ({ sectionTitle, fields, value, onFieldChange, patternColor }) => {
  const [open, setOpen] = useState(false);
  const cfg = SECTION_CONFIGS[sectionTitle] || SECTION_CONFIGS['Observation'];

  const filledCount = fields.filter(f => {
    const v = value[f.key];
    return Array.isArray(v) ? v.length > 0 : (v && v !== '');
  }).length;

  const abnormalCount = fields.filter(f => {
    const v = value[f.key];
    if (Array.isArray(v)) return v.length > 0 && v.some(o => !isNormalOpt(o));
    return v && v !== '' && !isNormalOpt(v);
  }).length;

  const allNormal = filledCount > 0 && abnormalCount === 0;

  const toggleMulti = (key, opt) => {
    const curr = value[key] || [];
    onFieldChange(key, curr.includes(opt) ? curr.filter(o => o !== opt) : [...curr, opt]);
  };

  const setSingle = (key, opt) => {
    onFieldChange(key, value[key] === opt ? '' : opt);
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: `2px solid ${cfg.borderColor}`,
          backgroundColor: cfg.bgColor, color: cfg.labelColor,
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + {cfg.icon} {sectionTitle}
          {filledCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: cfg.accentColor, color: '#fff', fontWeight: 800 }}>
              {filledCount}/{fields.length} fields
            </span>
          )}
          {abnormalCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#dc2626', color: '#fff', fontWeight: 800 }}>
              {abnormalCount} abnormal
            </span>
          )}
          {allNormal && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#059669', color: '#fff', fontWeight: 800 }}>
              All normal
            </span>
          )}
        </button>
      ) : (
        <div style={{ borderRadius: '16px', border: `2px solid ${cfg.borderColor}`, backgroundColor: cfg.bgColor, padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>{cfg.icon}</span>
              <span style={{ fontWeight: '800', fontSize: '1.05rem', color: cfg.labelColor }}>{sectionTitle}</span>
              {filledCount > 0 && (
                <span style={{
                  fontSize: '0.75rem', fontWeight: '700', borderRadius: '10px', padding: '2px 10px',
                  background: allNormal ? '#ecfdf5' : '#fef2f2',
                  color: allNormal ? '#059669' : '#dc2626',
                  border: `1px solid ${allNormal ? '#6ee7b7' : '#fca5a5'}`
                }}>
                  {allNormal ? '✓ All normal' : `${abnormalCount} abnormal finding${abnormalCount > 1 ? 's' : ''}`}
                </span>
              )}
            </div>
            <button type="button" onClick={() => setOpen(false)} style={{
              background: '#e2e8f0', border: 'none', borderRadius: '50%', width: '30px', height: '30px',
              cursor: 'pointer', fontWeight: '700', color: '#475569', fontSize: '1rem'
            }}>×</button>
          </div>

          {/* Fields */}
          {fields.map((field, fi) => {
            const fieldVal = value[field.key];
            const selected = Array.isArray(fieldVal) ? fieldVal : (fieldVal ? [fieldVal] : []);
            const hasAbnormal = selected.length > 0 && selected.some(o => !isNormalOpt(o));

            if (field.type !== 'multi' && field.type !== 'single') return null;

            return (
              <div key={field.key} style={{
                marginBottom: fi < fields.length - 1 ? '14px' : '0',
                paddingBottom: fi < fields.length - 1 ? '14px' : '0',
                borderBottom: fi < fields.length - 1 ? `1px solid ${cfg.borderColor}40` : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>{field.label}</div>
                  {hasAbnormal && (
                    <span style={{ fontSize: '10px', fontWeight: '700', color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '1px 7px' }}>
                      Abnormal
                    </span>
                  )}
                  {selected.length > 0 && !hasAbnormal && (
                    <span style={{ fontSize: '10px', fontWeight: '700', color: '#059669', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '1px 7px' }}>
                      Normal
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {field.options.map(opt => {
                    const isActive = field.type === 'multi'
                      ? (value[field.key] || []).includes(opt)
                      : value[field.key] === opt;
                    const isNorm = isNormalOpt(opt);
                    return (
                      <button key={opt} type="button"
                        onClick={() => field.type === 'multi' ? toggleMulti(field.key, opt) : setSingle(field.key, opt)}
                        style={{
                          padding: '8px 14px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '600',
                          minHeight: 44, cursor: 'pointer', transition: 'all 0.15s',
                          border: isActive
                            ? `2px solid ${isNorm ? '#059669' : patternColor || '#dc2626'}`
                            : '2px solid #e2e8f0',
                          background: isActive ? (isNorm ? '#ecfdf5' : `${patternColor || '#dc2626'}15`) : '#f8fafc',
                          color: isActive ? (isNorm ? '#065f46' : patternColor || '#dc2626') : '#64748b'
                        }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Clear */}
          {filledCount > 0 && (
            <button type="button"
              onClick={() => fields.forEach(f => onFieldChange(f.key, f.type === 'multi' ? [] : ''))}
              style={{ marginTop: '12px', padding: '6px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
              Clear All
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ENHANCED COLLAPSIBLE SENSORY ASSESSMENT
   ══════════════════════════════════════════════════════════════════════════ */

export default CollapsibleSectionAssessment;
